'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Check, LogIn, UserPlus, BookOpen, Shield, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

type Tab = 'login' | 'signup';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

export default function AuthScreen() {
  const [tab, setTab] = useState<Tab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupError, setSignupError] = useState('');

  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginForm>({ defaultValues: { remember: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { agreeTerms: false } });

  const onLogin = async (data: LoginForm) => {
    setLoginLoading(true);
    setLoginError('');
    try {
      await signIn(data.email, data.password);
      setLoginSuccess(true);
      setTimeout(() => router.push('/welcome'), 800);
    } catch (err: any) {
      setLoginError(err.message || 'Invalid email or password.');
    } finally {
      setLoginLoading(false);
    }
  };

  const onSignup = async (data: SignupForm) => {
    setSignupLoading(true);
    setSignupError('');
    try {
      await signUp(data.email, data.password, { fullName: data.username });
      setSignupSuccess(true);
    } catch (err: any) {
      setSignupError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background)' }}>
      {/* Left panel — brand */}
      <div
        className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[560px] flex-shrink-0 p-12 border-r stone-texture"
        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}
      >
        <div>
          <Link href="/" className="flex items-center gap-2.5 mb-16">
            <AppLogo size={36} />
            <span className="text-xl font-semibold" style={{ color: 'var(--foreground)' }}>
              Griott
            </span>
          </Link>

          <h2 className="text-3xl font-bold leading-tight mb-4" style={{ color: 'var(--foreground)' }}>
            History belongs to
            <br />
            <span className="gradient-text-ochre">everyone who lived it</span>
          </h2>
          <p className="text-sm leading-relaxed mb-10" style={{ color: 'var(--muted-foreground)' }}>
            Griott is a community-curated archive of Zimbabwean and African history. Every submission is reviewed by
            human curators before publication — because oral tradition, firsthand accounts, and sourced claims all
            matter differently.
          </p>

          <div className="space-y-4 mb-10">
            {[
              {
                icon: BookOpen,
                title: 'Three certainty labels',
                desc: 'Oral Tradition, Sourced Claim, and Firsthand Account — always shown publicly.',
              },
              {
                icon: Shield,
                title: 'Human moderation only',
                desc: 'Auto-triage adds flags, never changes status. A curator reviews every submission.',
              },
              {
                icon: Users,
                title: 'Community corroboration',
                desc: 'Readers confirm or dispute entries. Three disputes auto-flags for curator review.',
              },
            ].map((feat) => (
              <div key={`feat-${feat.title}`} className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: '#E05A4718', color: 'var(--primary)' }}
                >
                  <feat.icon size={15} />
                </div>
                <div>
                  <p className="text-sm font-medium mb-0.5" style={{ color: 'var(--foreground)' }}>
                    {feat.title}
                  </p>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                    {feat.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={28} />
            <span className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>
              Griott
            </span>
          </div>

          {/* Tab switcher */}
          <div
            className="flex rounded-xl p-1 mb-8"
            style={{ backgroundColor: 'var(--muted)' }}
          >
            {(['login', 'signup'] as Tab[]).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-150"
                style={{
                  backgroundColor: tab === t ? 'var(--card)' : 'transparent',
                  color: tab === t ? 'var(--foreground)' : 'var(--muted-foreground)',
                  boxShadow: tab === t ? '0 1px 3px #00000030' : 'none',
                }}
              >
                {t === 'login' ? <LogIn size={14} /> : <UserPlus size={14} />}
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* LOGIN FORM */}
          {tab === 'login' && (
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5">
              <div>
                <label
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: 'var(--foreground)' }}
                >
                  Email address
                </label>
                <input
                  type="email"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                  })}
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150"
                  style={{
                    backgroundColor: 'var(--input)',
                    borderColor: loginForm.formState.errors.email ? 'var(--status-rejected)' : 'var(--border)',
                    color: 'var(--foreground)',
                  }}
                  onFocus={(e) => {
                    if (!loginForm.formState.errors.email) {
                      (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)';
                    }
                  }}
                  onBlur={(e) => {
                    if (!loginForm.formState.errors.email) {
                      (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)';
                    }
                  }}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                    Password
                  </label>
                  <Link
                    href="/reset-password"
                    className="text-xs transition-colors duration-150"
                    style={{ color: 'var(--primary)' }}
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                    placeholder="••••••••"
                    className="w-full px-4 py-2.5 pr-10 rounded-lg border text-sm outline-none transition-colors duration-150"
                    style={{
                      backgroundColor: 'var(--input)',
                      borderColor: loginForm.formState.errors.password ? 'var(--status-rejected)' : 'var(--border)',
                      color: 'var(--foreground)',
                    }}
                    onFocus={(e) => {
                      if (!loginForm.formState.errors.password) {
                        (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)';
                      }
                    }}
                    onBlur={(e) => {
                      if (!loginForm.formState.errors.password) {
                        (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)';
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'var(--muted-foreground)' }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  {...loginForm.register('remember')}
                  className="w-4 h-4 rounded accent-primary"
                />
                <label htmlFor="remember" className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  Remember me for 30 days
                </label>
              </div>

              {loginError && (
                <p className="text-xs px-3 py-2 rounded-lg border" style={{ color: 'var(--status-rejected)', borderColor: '#E0707044', backgroundColor: '#E0707011' }}>
                  {loginError}
                </p>
              )}

              {loginSuccess ? (
                <div
                  className="flex items-center gap-2 px-4 py-3 rounded-lg border text-sm"
                  style={{ backgroundColor: '#5B9E6B22', borderColor: '#5B9E6B44', color: '#7DC48D' }}
                >
                  <Check size={16} />
                  Signed in successfully — redirecting…
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full py-3 rounded-lg font-semibold text-sm transition-all duration-150 active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  {loginLoading ? (
                    <>
                      <div
                        className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: 'var(--primary-foreground)' }}
                      />
                      Signing in…
                    </>
                  ) : (
                    <>
                      <LogIn size={16} />
                      Sign In
                    </>
                  )}
                </button>
              )}
            </form>
          )}

          {/* SIGNUP FORM */}
          {tab === 'signup' && (
            <>
              {signupSuccess ? (
                <div className="text-center py-8">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
                    style={{ backgroundColor: '#5B9E6B22', color: '#7DC48D' }}
                  >
                    <Check size={26} />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>Check your email</h3>
                  <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                    We&apos;ve sent a verification link to <strong style={{ color: 'var(--foreground)' }}>{signupForm.getValues('email')}</strong>. Click the link to activate your account.
                  </p>
                  <button
                    onClick={() => { setSignupSuccess(false); setTab('login'); }}
                    className="text-sm"
                    style={{ color: 'var(--primary)' }}
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      Username
                    </label>
                    <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      This will be your public contributor name in the archive.
                    </p>
                    <input
                      type="text"
                      {...signupForm.register('username', {
                        required: 'Username is required',
                        minLength: { value: 3, message: 'At least 3 characters' },
                        pattern: { value: /^[a-zA-Z0-9_.-]+$/, message: 'Only letters, numbers, _ . -' },
                      })}
                      placeholder="e.g. rutendo_m"
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150"
                      style={{
                        backgroundColor: 'var(--input)',
                        borderColor: signupForm.formState.errors.username ? 'var(--status-rejected)' : 'var(--border)',
                        color: 'var(--foreground)',
                      }}
                      onFocus={(e) => {
                        if (!signupForm.formState.errors.username) {
                          (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)';
                        }
                      }}
                      onBlur={(e) => {
                        if (!signupForm.formState.errors.username) {
                          (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)';
                        }
                      }}
                    />
                    {signupForm.formState.errors.username && (
                      <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                        {signupForm.formState.errors.username.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      Email address
                    </label>
                    <input
                      type="email"
                      {...signupForm.register('email', {
                        required: 'Email is required',
                        pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                      })}
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150"
                      style={{
                        backgroundColor: 'var(--input)',
                        borderColor: signupForm.formState.errors.email ? 'var(--status-rejected)' : 'var(--border)',
                        color: 'var(--foreground)',
                      }}
                      onFocus={(e) => {
                        if (!signupForm.formState.errors.email) {
                          (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)';
                        }
                      }}
                      onBlur={(e) => {
                        if (!signupForm.formState.errors.email) {
                          (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)';
                        }
                      }}
                    />
                    {signupForm.formState.errors.email && (
                      <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                        {signupForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        {...signupForm.register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Minimum 8 characters' },
                        })}
                        placeholder="Minimum 8 characters"
                        className="w-full px-4 py-2.5 pr-10 rounded-lg border text-sm outline-none transition-colors duration-150"
                        style={{
                          backgroundColor: 'var(--input)',
                          borderColor: signupForm.formState.errors.password ? 'var(--status-rejected)' : 'var(--border)',
                          color: 'var(--foreground)',
                        }}
                        onFocus={(e) => {
                          if (!signupForm.formState.errors.password) {
                            (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)';
                          }
                        }}
                        onBlur={(e) => {
                          if (!signupForm.formState.errors.password) {
                            (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.password && (
                      <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                        {signupForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      Confirm password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        {...signupForm.register('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: (val) =>
                            val === signupForm.watch('password') || 'Passwords do not match',
                        })}
                        placeholder="••••••••"
                        className="w-full px-4 py-2.5 pr-10 rounded-lg border text-sm outline-none transition-colors duration-150"
                        style={{
                          backgroundColor: 'var(--input)',
                          borderColor: signupForm.formState.errors.confirmPassword
                            ? 'var(--status-rejected)'
                            : 'var(--border)',
                          color: 'var(--foreground)',
                        }}
                        onFocus={(e) => {
                          if (!signupForm.formState.errors.confirmPassword) {
                            (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--primary)';
                          }
                        }}
                        onBlur={(e) => {
                          if (!signupForm.formState.errors.confirmPassword) {
                            (e.currentTarget as HTMLInputElement).style.borderColor = 'var(--border)';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {signupForm.formState.errors.confirmPassword && (
                      <p className="text-xs mt-1" style={{ color: 'var(--status-rejected)' }}>
                        {signupForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      {...signupForm.register('agreeTerms', {
                        required: 'You must agree to the terms',
                      })}
                      className="w-4 h-4 mt-0.5 rounded accent-primary"
                    />
                    <label htmlFor="agreeTerms" className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      I agree to the{' '}
                      <Link href="/terms-of-use" className="underline" style={{ color: 'var(--primary)' }}>
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link href="/privacy-policy" className="underline" style={{ color: 'var(--primary)' }}>
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  {signupForm.formState.errors.agreeTerms && (
                    <p className="text-xs -mt-3" style={{ color: 'var(--status-rejected)' }}>
                      {signupForm.formState.errors.agreeTerms.message}
                    </p>
                  )}

                  {signupError && (
                    <p className="text-xs px-3 py-2 rounded-lg border" style={{ color: 'var(--status-rejected)', borderColor: '#E0707044', backgroundColor: '#E0707011' }}>
                      {signupError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={signupLoading}
                    className="w-full py-3 rounded-lg font-semibold text-sm transition-all duration-150 active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  >
                    {signupLoading ? (
                      <>
                        <div
                          className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: 'var(--primary-foreground)' }}
                        />
                        Creating account…
                      </>
                    ) : (
                      <>
                        <UserPlus size={16} />
                        Create Account
                      </>
                    )}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}